'use client';
import React, { useRef, useState, useCallback } from 'react';
import { useClickOutside } from '../../../utils/hooks/use-click-outside.mjs';
import '@floating-ui/react';
import { getFloatingPosition } from '../Floating/get-floating-position/get-floating-position.mjs';
import '../Floating/FloatingArrow/FloatingArrow.mjs';
import { PopoverContextProvider } from './Popover.context.mjs';
import { usePopover } from './Popover.utils.mjs';
import { PopoverDropdown } from './PopoverDropdown/PopoverDropdown.mjs';
import { PopoverTarget } from './PopoverTarget/PopoverTarget.mjs';

function Popover(props) {
  const {
    children,
    position = "bottom",
    offset = 8,
    onPositionChange,
    opened,
    width = "max-content",
    middlewares = { flip: true, shift: true, inline: false },
    withArrow,
    arrowPosition = "side",
    closeOnClickOutside = true,
    withinPortal = true,
    closeOnEscape = true,
    clickOutsideEvents = ["mousedown", "touchstart"],
    trapFocus = true,
    onClose,
    onOpen,
    onChange,
    zIndex = 300,
    id = "popover",
    dropdownId,
    defaultOpened = false,
    withRoles = true,
    disabled,
    returnFocus = false,
    keepMounted,
    floatingStrategy,
    ...rest
  } = props;
  const positionDependencies = [];
  const arrowOffset = 15;
  const arrowSize = 12;
  const arrowRef = useRef(null);
  const [targetNode, setTargetNode] = useState(null);
  const [dropdownNode, setDropdownNode] = useState(null);
  const popover = usePopover({
    middlewares,
    width,
    position: getFloatingPosition("ltr", position),
    offset: typeof offset === "number" ? offset + (withArrow ? arrowSize / 2 : 0) : offset,
    arrowRef,
    arrowOffset,
    onPositionChange,
    positionDependencies,
    opened,
    defaultOpened,
    onChange,
    onOpen,
    onClose,
    strategy: floatingStrategy
  });
  useClickOutside(() => closeOnClickOutside && popover.onClose(), clickOutsideEvents, [targetNode, dropdownNode]);
  const reference = useCallback(
    (node) => {
      setTargetNode(node);
      popover.floating.refs.setReference(node);
    },
    [popover.floating.refs.setReference]
  );
  const floating = useCallback(
    (node) => {
      setDropdownNode(node);
      popover.floating.refs.setFloating(node);
    },
    [popover.floating.refs.setFloating]
  );
  return /* @__PURE__ */ React.createElement(
    PopoverContextProvider,
    {
      value: {
        returnFocus,
        disabled,
        controlled: popover.controlled,
        reference,
        floating,
        x: popover.floating.x,
        y: popover.floating.y,
        arrowX: popover.floating?.middlewareData?.arrow?.x,
        arrowY: popover.floating?.middlewareData?.arrow?.y,
        opened: popover.opened,
        arrowRef,
        width,
        withArrow,
        arrowSize,
        arrowOffset,
        arrowRadius: 1,
        arrowPosition,
        placement: popover.floating.placement,
        trapFocus,
        withinPortal,
        zIndex,
        closeOnEscape,
        onClose: popover.onClose,
        onToggle: popover.onToggle,
        getTargetId: () => `${id}-target`,
        getDropdownId: () => dropdownId || `${id}-dropdown`,
        withRoles,
        targetProps: rest,
        keepMounted
      }
    },
    children
  );
}
Popover.Target = PopoverTarget;
Popover.Dropdown = PopoverDropdown;

export { Popover };
